/**
 * Web Push helper — handles VAPID key fetch, service-worker registration,
 * permission request, PushManager.subscribe, and posting the subscription
 * to the backend.
 *
 * Usage:
 *   await ensurePushRegistered(token);       // after login, idempotent
 *   await disablePush(token);                // user toggled OFF
 *   await sendTestPush(token);               // QA helper
 */
import axios from "axios";
import { API } from "@/App";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return typeof window !== "undefined" &&
         "serviceWorker" in navigator &&
         "PushManager"  in window &&
         "Notification" in window;
}

async function getSWRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  // Register at root scope so it can control the whole app.
  let reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;
  return reg;
}

/**
 * Ask the user for permission (if not already granted), then subscribe
 * via the browser's PushManager using our VAPID public key, and POST
 * the subscription to the backend. Idempotent — safe to call on every
 * login or page load.
 */
export async function ensurePushRegistered(token) {
  if (!pushSupported() || !token) return { ok: false, reason: "unsupported" };
  // Permission is requested in response to a user gesture — for "auto" mode
  // we still attempt to read existing permission first.
  if (Notification.permission === "denied") return { ok: false, reason: "denied" };
  if (Notification.permission === "default") {
    const res = await Notification.requestPermission();
    if (res !== "granted") return { ok: false, reason: res };
  }
  const reg = await getSWRegistration();
  if (!reg) return { ok: false, reason: "no-sw" };
  const { data } = await axios.get(`${API}/push/vapid-public-key`);
  const pubKey = data?.public_key;
  if (!pubKey) return { ok: false, reason: "no-key" };
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(pubKey),
    });
  }
  // Extract keys + endpoint and persist on the backend.
  const raw = sub.toJSON();
  await axios.post(`${API}/push/subscribe`, {
    endpoint:   raw.endpoint,
    p256dh:     raw.keys.p256dh,
    auth:       raw.keys.auth,
    user_agent: navigator.userAgent,
    provider:   "webpush",
  }, { headers: { Authorization: `Bearer ${token}` } });
  return { ok: true, endpoint: raw.endpoint };
}

export async function disablePush(token) {
  try {
    const reg = await getSWRegistration();
    if (!reg) return { ok: true };
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      try {
        await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint },
          { headers: { Authorization: `Bearer ${token}` } });
      } catch { /* best-effort */ }
      await sub.unsubscribe();
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: String(e) };
  }
}

export async function sendTestPush(token) {
  const r = await axios.post(`${API}/push/test`, {},
    { headers: { Authorization: `Bearer ${token}` } });
  return r.data;
}

export async function getCurrentSubscriptionEndpoint() {
  const reg = await getSWRegistration();
  if (!reg) return null;
  const s = await reg.pushManager.getSubscription();
  return s ? s.endpoint : null;
}
